import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import type {
  ImageUploadCopy,
  ImageUploadOnPublishData,
} from '@xing-com/crate-entity-pages-common';
import {
  ImageUpload,
  WarningMessageBox as WarningBox,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { Headline } from '@xing-com/typography';

import { EntityPageOriginalLogoDocument } from '../../../../graphql/queries/original-logo.gql-types';
import { CropSkeleton } from '../../../crop-skeleton/crop-skeleton';

import * as Styled from './update-logo.styles';

const ErrorFallback: FC<{ onReloadClick?: () => void }> = ({
  onReloadClick = () => window.location.reload(),
}) => {
  return (
    <>
      <Headline size="xxlarge">
        <Styled.UploadHeadline>
          <FormattedMessage id="EP_CHOOSE_SECTION" />
        </Styled.UploadHeadline>
      </Headline>
      <WarningBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={() => onReloadClick()}
      />
    </>
  );
};

type UpdateLogoProps = {
  isCover?: boolean;
  descriptionCopyKey?: string;
  aspectRatio?: number;
  imageUploadCopy?: ImageUploadCopy;
  onCropReady?: () => void;
  onPublish?: (data: ImageUploadOnPublishData) => void;
  onDragCrop?: () => void;
  onDiscard: () => void;
  onCancel: () => void;
};

const UpdateLogoInner: FC<UpdateLogoProps> = ({
  descriptionCopyKey,
  aspectRatio,
  imageUploadCopy,
  onCropReady = () => undefined,
  onPublish = () => undefined,
  onDragCrop = () => undefined,
  onDiscard,
  onCancel,
}) => {
  const { pageContext } = usePageContext();

  const { data, error, refetch } = useSuspenseQuery(
    EntityPageOriginalLogoDocument,
    {
      fetchPolicy: 'no-cache',
      variables: {
        id: pageContext.pageSlug,
        dimensions: [{ width: 310, height: 310, reference: 'logo' }],
      },
    }
  );

  const entityPage =
    data?.entityPageEX?.__typename === 'EntityPage'
      ? data?.entityPageEX
      : undefined;

  const originalImage = entityPage?.originalLogoImage?.[0]?.url;

  if (!originalImage || error) {
    return <ErrorFallback onReloadClick={() => refetch()} />;
  }

  return (
    <ImageUpload
      descriptionCopyKey={descriptionCopyKey}
      onCropReady={onCropReady}
      onDragCrop={onDragCrop}
      onDiscard={onDiscard}
      defaultImageCrop={originalImage}
      onPublish={onPublish}
      aspectRatio={aspectRatio}
      onCancel={onCancel}
      imageUploadCopy={imageUploadCopy}
    />
  );
};

const UpdateLogoFallback: FC<{ isCover: boolean }> = ({ isCover }) => {
  return (
    <>
      <Headline size="xxlarge">
        <Styled.UploadHeadline>
          <FormattedMessage id="EP_CHOOSE_SECTION" />
        </Styled.UploadHeadline>
      </Headline>
      <CropSkeleton isCover={isCover} />
    </>
  );
};

export const UpdateLogo: FC<UpdateLogoProps> = ({
  isCover = false,
  ...props
}) => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<UpdateLogoFallback isCover={isCover} />}>
        <UpdateLogoInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
